// Contenido estructurado de la formación para admin, kiosko y app móvil.
// Se importa desde FormacionTab.js. Mantenerlo aquí facilita futuras traducciones
// y evita que el componente sea gigante.

export const CURSOS = {
  admin: {
    slug: 'admin',
    titulo: 'Administración',
    subtitulo: 'Panel de gestión de la empresa',
    dirigido_a: 'Administradores y responsables de RRHH',
    color: 'from-zinc-900 to-zinc-700',
    accent: 'zinc',
    duracion_est: '20-30 min',
    pdf: '/formacion/manual_admin.pdf',
    intro: 'Este curso recorre paso a paso todas las funcionalidades del panel de administración, ordenadas por la primera vez que las necesitas para poner el sistema en marcha en tu empresa.',
    prerequisitos: [
      'Un email por cada empleado (obligatorio)',
      'DNI / NIE de cada empleado (se valida la letra)',
      'PIN de 4-6 dígitos por empleado',
      'La dirección exacta de cada centro (si vas a validar por GPS)',
    ],
    modulos: [
      {
        numero: 1,
        titulo: 'Acceso al panel',
        objetivo: 'Entrar por primera vez y familiarizarte con la barra superior.',
        pasos: [
          'Abre <code>https://tudominio/admin/login</code> en tu navegador.',
          'Introduce tu <b>usuario administrador</b> y contraseña iniciales (te los da el comercial o el superadministrador que dio de alta tu empresa).',
          'Pulsa <b>Acceder</b>.',
          'En cuanto entres, ve a <b>Ajustes → Seguridad</b> y cambia la contraseña por una tuya.',
        ],
        imagen: '/formacion/img/admin_01_login.png',
        tip: 'Si accedes por primera vez, cambia la contraseña por seguridad. El superadmin nunca la verá.',
      },
      {
        numero: 2,
        titulo: 'Pestaña Resumen',
        objetivo: 'Ver de un vistazo el estado del día.',
        pasos: [
          'KPIs del día: empleados activos, quiénes trabajan ahora, quiénes en descanso y horas totales.',
          'Incidencias de ubicación: fichajes desde la app fuera del radio del centro. Toca el bloque para revisarlos.',
          'Tabla "Estado del personal hoy": ver quién ha entrado, a qué hora, cuánto lleva y su estado actual.',
        ],
        imagen: '/formacion/img/admin_02_resumen.png',
        tip: 'Descarga el CSV de incidencias GPS pulsando el icono de descarga.',
      },
      {
        numero: 3,
        titulo: 'Centros de trabajo',
        objetivo: 'Crear al menos un centro con su ubicación y calendario propio.',
        pasos: [
          'Rellena <b>Nombre</b> y elige la <b>Comunidad Autónoma</b> (necesaria para los festivos oficiales).',
          'Añade municipio, provincia y dirección (opcionales pero recomendados para la geolocalización).',
          'En "Ubicación GPS": pulsa <b>Usar la dirección</b> para calcular las coordenadas, o <b>Mi ubicación</b> si estás en el centro.',
          'Ajusta el <b>Radio permitido</b> (100 m oficina, 300 m nave grande, vacío para reparto/comerciales).',
          'Pulsa <b>Crear centro</b>.',
        ],
        imagen: '/formacion/img/admin_03_centros.png',
        tip: 'Los fichajes fuera del radio se aceptan pero se marcan como "incidencia GPS" para que tú los revises.',
      },
      {
        numero: 4,
        titulo: 'Empleados',
        objetivo: 'Dar de alta, invitar y gestionar a tu equipo.',
        pasos: [
          'Completa el formulario con nombre, DNI (se valida la letra), email obligatorio y PIN.',
          'Elige jornada esperada (h/día), departamentos y centro.',
          'Casos especiales resueltos automáticamente: DNI dado de baja → diálogo <b>Reactivar</b>; DNI en otra empresa → permitido (multi-empresa real).',
          'Botones por empleado: Editar · Reenviar PIN (icono ámbar) · Enviar invitación a la app · Desactivar/Activar.',
        ],
        imagen: '/formacion/img/admin_04_empleados.png',
        tip: 'Reenviar PIN genera uno nuevo aleatorio de 6 dígitos y lo envía por email. Si no hay email, te lo muestra para que se lo des en persona.',
      },
      {
        numero: 5,
        titulo: 'Turnos',
        objetivo: 'Definir las plantillas de turno de tu empresa.',
        pasos: [
          'Crea plantillas de turno (Mañana 09-17, Tarde 15-23, Nocturno 22-06, etc.).',
          'Cuando un empleado ficha, el sistema clasifica automáticamente su fichaje en el turno correspondiente.',
          'Turnos nocturnos: el sistema entiende jornadas que cruzan medianoche como una única jornada.',
        ],
        imagen: '/formacion/img/admin_05_turnos.png',
      },
      {
        numero: 6,
        titulo: 'Calendario laboral',
        objetivo: 'Cargar festivos y registrar ausencias por centro.',
        pasos: [
          'Elige el <b>centro</b> en el selector superior.',
          'Pulsa <b>Importar oficial</b> para traer festivos nacionales + autonómicos del año en un clic.',
          'Alternativa: sube un fichero <code>.ics</code> o <code>CSV</code> del ayuntamiento, o duplica de otro centro.',
          'Justo debajo, registra ausencias: vacaciones, baja médica, permiso. Aparecerán en informes.',
        ],
        imagen: '/formacion/img/admin_07_calendario.png',
        tip: 'Los tipos de festivo (nacional / autonómico / local / empresa) tienen color propio para distinguirlos.',
      },
      {
        numero: 7,
        titulo: 'Fichajes del día',
        objetivo: 'Corregir olvidos y errores puntuales.',
        pasos: [
          'Filtra por día y empleado para ver todos los fichajes registrados.',
          'Icono ➕ <b>Añadir fichaje manual</b>: para un fichaje olvidado. Queda trazado en auditoría.',
          'Icono ✏️ <b>Editar</b>: cambia hora o tipo. Igual: queda en auditoría.',
          'Icono 🗑️ <b>Borrar</b>: quita el fichaje. También trazado.',
        ],
        imagen: '/formacion/img/admin_06_fichajes.png',
        aviso: 'Normativa 2026: cualquier cambio manual sobre un fichaje debe ser aceptado por el empleado desde su app. Mientras no lo acepte, aparece como "Pendiente de validación" en la auditoría.',
      },
      {
        numero: 8,
        titulo: 'Comunicados',
        objetivo: 'Publicar avisos para todos los empleados.',
        pasos: [
          'Publica avisos que aparecerán al fichar en el kiosko o al abrir la app móvil.',
          'Ideal para: cambios de horario, reuniones, cortes de agua, felicitaciones.',
          'Marca un comunicado como <b>destacado</b> para que salga en primer plano.',
        ],
        imagen: '/formacion/img/admin_08_comunicados.png',
      },
      {
        numero: 9,
        titulo: 'Informes y auditoría',
        objetivo: 'Sacar informes por empleado y revisar cambios.',
        pasos: [
          'Elige un rango de fechas. Por cada empleado verás: horas trabajadas, diferencia con lo esperado, descansos, ausencias, festivos.',
          '<b>Exportar CSV</b> por empleado o <b>PDF</b> consolidado.',
          'Al final de la pestaña, el <b>Registro de auditoría</b> con todos los cambios manuales sobre fichajes.',
          'Filtros: búsqueda por texto, rango de fechas, acción (crear/editar/eliminar), estado (pendiente/aceptado/rechazado).',
          '<b>Exportar CSV</b> del auditoría: formato Excel-friendly (BOM UTF-8, separador ;).',
        ],
        imagen: '/formacion/img/admin_09_informes.png',
      },
      {
        numero: 10,
        titulo: 'Ajustes de la empresa',
        objetivo: 'Personalizar la marca, activar kioskos y gestionar seguridad.',
        pasos: [
          '<b>Marca</b>: logo, color corporativo (aparece en la app del empleado).',
          '<b>Seguridad</b>: cambia tu contraseña de administrador desde la tarjeta "Cambiar contraseña".',
          '<b>Auto-cierre de jornadas olvidadas</b>: actívalo y elige un umbral (por defecto 12 h). El empleado deberá aceptar cualquier cierre automático desde su app.',
          '<b>Colaboradores</b>: da acceso a personas de tu equipo con rol <i>Colaborador</i>, <i>Gestoría</i> o <i>Auditor</i>. Usa el botón sobre-envelope para <b>enviar la invitación por email</b>.',
          '<b>Kiosko</b>: activa una tablet como kiosko de fichaje (URL + credenciales).',
        ],
        imagen: '/formacion/img/admin_10_ajustes.png',
      },
    ],
    faq: [
      {
        q: '¿Puede un empleado estar en varias empresas?',
        a: 'Sí. Cada empresa gestiona su propio alta. Al hacer login en la app, si el DNI existe en varias, el empleado elige la empresa.',
      },
      {
        q: 'Un empleado no recibe el email de invitación',
        a: '1) Verifica en el panel superadmin → Correo que el SMTP está guardado y prueba "Enviar email de prueba". 2) Si funciona pero no le llega, revisa spam. 3) Plan B: usa "Copiar enlace" y envíaselo por WhatsApp.',
      },
      {
        q: 'Aparece "WebAuthn no soportado" al activar la huella',
        a: 'La app móvil debe abrirse en un navegador real (Chrome/Safari), no dentro del visor de correo. Que la instale como PWA con "Instalar app" y la abra desde el icono del móvil.',
      },
      {
        q: 'Un fichaje aparece con "incidencia GPS"',
        a: 'El empleado fichó fuera del radio del centro. Míralo en Resumen → Incidencias, revisa el mapa y decide si lo aceptas (era un motivo válido: reparto, cita fuera) o pides al empleado que corrija.',
      },
    ],
  },

  kiosko: {
    slug: 'kiosko',
    titulo: 'Kiosko',
    subtitulo: 'Tablet compartida del centro',
    dirigido_a: 'Empleados que fichan en la tablet común',
    color: 'from-blue-700 to-blue-500',
    accent: 'blue',
    duracion_est: '10 min',
    pdf: '/formacion/manual_kiosko.pdf',
    intro: 'Con el kiosko, todos los empleados del centro fichan en la misma tablet. Solo necesitas tu PIN (los 4-6 dígitos que te dio tu responsable).',
    prerequisitos: [
      'Tener tu PIN activo (te lo comunica el administrador)',
      'La tablet debe estar ya activada como kiosko (lo hace el administrador)',
    ],
    modulos: [
      {
        numero: 1,
        titulo: 'Elegir tu nombre en la cuadrícula',
        objetivo: 'Localizarte entre tus compañeros al llegar a la tablet.',
        pasos: [
          'La tablet muestra una <b>cuadrícula con las fotos y nombres</b> de todos los compañeros del centro.',
          'Toca tu tarjeta para empezar.',
        ],
        imagen: '/formacion/img/kiosko_02_grid_empleados.png',
        aviso: 'Si en lugar de esto ves una pantalla "Usuario / Contraseña", pídeselo al responsable: la tablet aún no está activada como kiosko.',
      },
      {
        numero: 2,
        titulo: 'Introducir tu PIN',
        objetivo: 'Autenticarte con tus 4-6 dígitos.',
        pasos: [
          'Aparece un teclado numérico grande.',
          'Teclea tu PIN y pulsa el botón verde de confirmar.',
        ],
        imagen: '/formacion/img/kiosko_03_pin.png',
        tip: '¿Olvidaste el PIN? Habla con tu responsable, puede generarte uno nuevo en un clic y enviártelo por email.',
      },
      {
        numero: 3,
        titulo: 'Fichar la acción disponible',
        objetivo: 'Fichar entrada, descanso o salida según tu estado actual.',
        pasos: [
          'El kiosko muestra <b>solo la acción que puedes hacer ahora</b> en un botón grande.',
          'Nunca te permitirá fichar dos entradas seguidas ni una salida sin haber entrado antes.',
          'Al pulsar el botón, verás una confirmación con la hora exacta y volverás a la pantalla principal.',
        ],
        imagen: '/formacion/img/kiosko_04_acciones.png',
        tabla: {
          cabeceras: ['Estado actual', 'Acción disponible'],
          filas: [
            ['Sin fichar', 'Fichar Entrada'],
            ['Trabajando', 'Iniciar Descanso · Fichar Salida'],
            ['En descanso', 'Fin Descanso'],
            ['Jornada finalizada', 'Solo entrada del siguiente turno'],
          ],
        },
      },
      {
        numero: 4,
        titulo: 'Consultar mis fichajes',
        objetivo: 'Ver el histórico personal desde el kiosko.',
        pasos: [
          'En la pantalla de acciones, pulsa <b>Ver mis fichajes</b>.',
          'Verás fecha, hora, tipo, origen (kiosko/app/manual) y horas trabajadas por día.',
          'Puedes descargar tus fichajes del mes en PDF con el icono de descarga.',
        ],
      },
      {
        numero: 5,
        titulo: 'Aceptar o rechazar cambios (Normativa 2026)',
        objetivo: 'Validar los cambios que tu responsable haya hecho.',
        pasos: [
          'Si el responsable añade, edita o borra un fichaje tuyo, verás un <b>aviso de revisión</b> antes de las acciones normales.',
          'Se te muestra el cambio (qué fichaje, qué se modificó, motivo).',
          'Pulsa <b>Aceptar</b> ✅ o <b>Rechazar</b> ❌ dando un motivo si es incorrecto.',
        ],
        aviso: 'Es obligatorio por la nueva normativa española de registro horario 2026. Ningún cambio manual sobre tus fichajes es válido sin tu confirmación.',
      },
    ],
    faq: [
      {
        q: 'Me sale "PIN incorrecto" tres veces y ya no me deja probar',
        a: 'Por seguridad, tras varios intentos fallidos se bloquean los intentos unos minutos. Espera y prueba de nuevo. Si no lo recuerdas, pídele al responsable que te lo reenvíe.',
      },
      {
        q: 'No aparezco en la cuadrícula',
        a: 'Tu responsable puede haberte asignado a otro centro, haberte desactivado o aún no haberte dado de alta. Habla con él.',
      },
      {
        q: 'El kiosko no responde',
        a: 'Pide ayuda al responsable: puede desbloquear la tablet con sus credenciales de administrador.',
      },
    ],
  },

  app: {
    slug: 'app',
    titulo: 'App móvil',
    subtitulo: 'Fichaje desde tu propio teléfono',
    dirigido_a: 'Empleados que fichan en movilidad (comerciales, técnicos, teletrabajo)',
    color: 'from-emerald-700 to-emerald-500',
    accent: 'emerald',
    duracion_est: '15 min',
    pdf: '/formacion/manual_app_movil.pdf',
    intro: 'Con la app puedes fichar desde tu móvil, ver tus fichajes, cambiar tu PIN y entrar con huella. No necesitas descargarla desde la Play Store: es una PWA (una web que se instala como app).',
    prerequisitos: [
      'Recibir el enlace de invitación por email de tu responsable',
      'Abrir el enlace en Chrome (Android) o Safari (iPhone), no dentro del correo',
      'Tu DNI y PIN activos',
    ],
    modulos: [
      {
        numero: 1,
        titulo: 'Instalar la app la primera vez',
        objetivo: 'Anclar la app en tu escritorio para abrirla como cualquier otra.',
        pasos: [
          'Abre el enlace de invitación en Chrome (Android) o Safari (iPhone), no dentro del email.',
          'En la pantalla principal verás una tarjeta azul <b>Instalar en el móvil</b>.',
          '<b>Android/Chrome</b>: pulsa "Instalar app" → confirma. El icono aparecerá en tu escritorio.',
          '<b>iPhone/Safari</b>: pulsa "Ver cómo instalarla" y sigue los 3 pasos (Compartir → Añadir a pantalla de inicio → Añadir).',
        ],
        imagen: '/formacion/img/app_02_dashboard.png',
        aviso: 'En Android, al pulsar el enlace desde Gmail muchas veces se abre dentro de Gmail. En ese modo la huella no funciona. Mantén pulsado el enlace → "Abrir en Chrome".',
      },
      {
        numero: 2,
        titulo: 'Iniciar sesión',
        objetivo: 'Entrar con tu DNI y PIN.',
        pasos: [
          'Introduce tu <b>DNI</b> y tu <b>PIN</b>. Pulsa Entrar.',
          'Si tu DNI existe en varias empresas, aparecerá una lista para que elijas la correcta.',
          'El botón "Entrar con huella" solo aparece tras activarla (ver módulo 4).',
        ],
        imagen: '/formacion/img/app_01_login.png',
      },
      {
        numero: 3,
        titulo: 'Fichar desde el móvil',
        objetivo: 'Registrar entrada, descanso o salida con o sin GPS.',
        pasos: [
          'En el panel principal ves la hora actual, tu nombre y estado.',
          'Un <b>botón grande</b> muestra la acción disponible (Fichar Entrada, Iniciar Descanso, etc.).',
          'Si tu empresa exige GPS: el móvil te pedirá permiso. Dentro del radio → fichaje normal. Fuera → se marca como "incidencia GPS" (tu responsable la revisa).',
          'Sin GPS: si tu empresa no lo ha configurado, no se pedirá ubicación.',
        ],
        tip: 'Al fichar salida, espera 2 segundos antes de cerrar la app para asegurar el envío.',
      },
      {
        numero: 4,
        titulo: 'Activar huella o Face ID',
        objetivo: 'Entrar con un toque en lugar de teclear el PIN.',
        pasos: [
          'En la pantalla principal, al pie, pulsa <b>Activar acceso con huella</b>.',
          'Android: te pedirá tu huella o Face ID (si el móvil lo soporta).',
          'iPhone: te pedirá Face ID o Touch ID.',
          'A partir de ese momento, en el login aparecerá el botón <b>Entrar con huella</b>.',
        ],
        tabla: {
          cabeceras: ['Requisito', 'Detalle'],
          filas: [
            ['Bloqueo de pantalla activo', 'PIN, patrón o huella del sistema'],
            ['Al menos una huella o Face ID registrada', 'En los ajustes del móvil'],
            ['Abrir la app desde el icono del escritorio', 'No desde el visor de correo'],
          ],
        },
        tip: 'Si sale "Activación cancelada": (1) estás en el visor de correo → ábrela en Chrome/Safari, (2) no tienes huella registrada en el móvil, (3) certificado del servidor con problema.',
      },
      {
        numero: 5,
        titulo: 'Mis fichajes, cambio de PIN y auto-cierres',
        objetivo: 'Autogestionar tu histórico y respuesta a correcciones.',
        pasos: [
          '<b>Mis fichajes</b>: 30 últimos días con fecha, tipo, origen y horas por día. Descarga PDF.',
          '<b>Cambiar PIN</b>: PIN actual → nuevo (2 veces) → 4-6 dígitos. La huella sigue funcionando.',
          '<b>Auto-cierres</b>: si olvidas fichar la salida, verás un modal al abrir la app con los detalles del cierre automático. Debes <b>Confirmar</b> o <b>Rechazar con motivo</b>.',
          '<b>Cambios del responsable</b>: si añade/edita/borra un fichaje, verás un aviso arriba. Acéptalo o recházalo (obligatorio por normativa 2026).',
        ],
      },
    ],
    faq: [
      {
        q: 'No me llega el email de invitación',
        a: 'Mira spam. Si aún así no llega, pídele a tu responsable que use "Copiar enlace" y te lo mande por WhatsApp.',
      },
      {
        q: '"Este dispositivo no soporta acceso con huella / Face ID"',
        a: 'Tu móvil no tiene sensor o no lo tienes configurado. La app sigue funcionando: entra con tu PIN.',
      },
      {
        q: 'Fiché la entrada y se cerró la app; ¿se ha guardado?',
        a: 'Abre "Mis fichajes". Si aparece, todo bien. Si no, vuelve a fichar entrada.',
      },
      {
        q: 'Cambio de móvil: ¿pierdo la huella?',
        a: 'Sí, está registrada en el dispositivo. En el nuevo: instala la app, entra con PIN y activa la huella otra vez.',
      },
    ],
  },
};
